@use "Styles/includes/variables";
@use "Styles/mixins";

:host {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 10px;

    @include mixins.desktop-and-up {
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-rows: auto auto;
        column-gap: 8px;
        align-items: stretch;
    }
}

*,
*::before,
*::after {
    box-sizing: inherit;
}

.vm-video-comment-composer {
    &__avatar {
        position: relative;
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 100%;
        overflow: hidden;

        &::after {
            content: " ";
            position: absolute;
            inset: 0;
            border-radius: 100%;
            border: 1px solid mixins.get-color(border, transparent, secondary);
        }

        &::part(initials) {
            font-size: mixins.get-font-size(text-md);
        }

        @include mixins.desktop-and-up {
            grid-column: 1;
            grid-row: 1;
            align-self: end;
        }
    }

    &__textarea {
        flex: 1 1 auto;
        min-width: 0;
        // Autogrow range: 1 row → 3 rows, then `overflow-y: auto` scrolls.
        min-height: mixins.get-line-height(text-md);
        max-height: mixins.get-line-height(text-md) * 3;
        border: none;
        outline: none;
        background: transparent;
        padding: 0;
        margin: 0;
        resize: none;
        overflow-y: auto;
        font-family: variables.$fontQuaternary;
        font-size: mixins.get-font-size(text-md);
        font-weight: 400;
        line-height: mixins.get-line-height(text-md);
        color: mixins.get-color(text, text-primary);

        &::placeholder {
            color: mixins.get-color(text, quaternary);
        }

        @include mixins.desktop-and-up {
            min-height: mixins.get-line-height(text-sm);
            max-height: mixins.get-line-height(text-sm) * 3;
            font-size: mixins.get-font-size(text-sm);
            line-height: mixins.get-line-height(text-sm);
            color: mixins.get-color(text, text-secondary-hover);

            &::placeholder {
                color: mixins.get-color(text, text-placeholder);
            }
        }
    }

    &__attach {
        flex-shrink: 0;
        border: none;
        background: transparent;
        color: mixins.get-color(foreground, fg-secondary);
        cursor: pointer;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        // Hidden in the idle pill — paperclip only appears once the pill
        // expands into the focused card.
        .vm-video-comment-composer__pill:not(.is-focused) & {
            display: none;
        }

        &--md {
            width: 24px;
            height: 24px;

            vm-icon-next {
                width: 24px;
                height: 24px;
            }
        }

        &--sm {
            width: 16px;
            height: 16px;

            vm-icon-next {
                width: 16px;
                height: 16px;
            }
        }
    }

    &__send {
        flex-shrink: 0;

        // Make the disabled send button click-through so the wrapper's mousedown handler runs.
        &[disabled] {
            pointer-events: none;
        }
    }

    // -----------------------------------------------------------------------
    // Mobile pill (idle) ↔ rounded card (focused).
    // -----------------------------------------------------------------------
    &__pill {
        display: flex;
        align-items: center;
        flex: 1 1 auto;
        min-width: 0;
        min-height: 54px;
        padding: 8px 13px 8px 20px;
        gap: 16px;
        border-radius: 100px;
        background: mixins.get-color(background, bg-white);
        box-shadow:
            0 3px 3px rgba(10, 13, 18, 0.04),
            0 8px 8px rgba(10, 13, 18, 0.03),
            0 20px 24px rgba(10, 13, 18, 0.08),
            0 -3px 3px rgba(10, 13, 18, 0.04);

        &.is-focused {
            flex-direction: column;
            align-items: stretch;
            min-height: 0;
            padding: 16px 16px 8px;
            gap: 8px;
            border-radius: 24px;
        }
    }

    &__main {
        .vm-video-comment-composer__pill:not(.is-focused) & {
            display: contents;
        }

        .vm-video-comment-composer__pill.is-focused & {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;

        .vm-video-comment-composer__pill.is-focused & {
            justify-content: space-between;
        }
    }

    // -----------------------------------------------------------------------
    // Chip strip — shared between mobile (horizontal scroll under textarea)
    // and desktop (3-column grid to the right of the textarea).
    // -----------------------------------------------------------------------
    &__chips {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 6px 0 8px 0;
        margin: 0;
        list-style: none;
        border-bottom: 1px solid mixins.get-color(border, border-primary);
        overflow-x: auto;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }

        @include mixins.desktop-and-up {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-end;
            width: calc(64px * 3 + 12px * 2);
            padding: 0;
            border-bottom: none;
            overflow: visible;
        }
    }

    &__chip {
        position: relative;
        flex-shrink: 0;
        width: 64px;
        height: 64px;
    }

    &__chip-thumb {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 16px;
        border: 1px solid mixins.get-color(border, transparent, secondary);

        &--file {
            display: flex;
            align-items: center;
            justify-content: center;
            background: mixins.get-color(background, bg-gray-150);
            color: mixins.get-color(foreground, fg-tertiary);
        }
    }

    &__chip-uploading {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: mixins.get-color(background, bg-white-alpha70);
        border-radius: 16px;
        color: mixins.get-color(foreground, fg-primary);
        pointer-events: none;
    }

    &__chip-remove {
        position: absolute;
        top: -6px;
        right: -6px;
        width: 18px;
        height: 18px;
        background-color: mixins.get-color(background, bg-white);
        border: none;
        border-radius: 50%;
        color: mixins.get-color(foreground, fg-primary);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 0;
    }

    // -----------------------------------------------------------------------
    // Desktop composer — underline field + Post-button row.
    // -----------------------------------------------------------------------
    &__composer {
        @include mixins.desktop-and-up {
            display: contents;
        }

        &.is-focused {
            @include mixins.desktop-and-up {
                .vm-video-comment-composer__field {
                    border-bottom-color: mixins.get-color(background, bg-brand-solid);
                    gap: 12px;
                }
            }
        }
    }

    &__field {
        @include mixins.desktop-and-up {
            grid-column: 2;
            grid-row: 1;
            display: flex;
            align-items: flex-end;
            gap: 10px;
            min-height: 36px;
            padding-bottom: 8px;
            border-bottom: 1px solid mixins.get-color(foreground, fg-disabled-subtle);
        }
    }

    &__field-right {
        @include mixins.desktop-and-up {
            display: flex;
            align-items: flex-end;
            flex-shrink: 0;
            gap: 12px;

            // Anchors the paperclip to the bottom of the right column so it
            // sits next to the underline even when chips are absent.
            > .vm-video-comment-composer__attach {
                margin-top: auto;
            }
        }
    }

    &__post-row {
        @include mixins.desktop-and-up {
            grid-column: 2;
            grid-row: 2;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
        }
    }

    &__reply-chip {
        display: flex;
        align-items: center;
        gap: 4px;
        align-self: flex-start;
        padding: 0;
        border: none;
        background: transparent;
        font-size: 12px;
        line-height: 1.2;
        color: mixins.get-color(text, text-secondary);
        cursor: pointer;

        &[disabled] {
            cursor: default;
        }

        vm-icon-next {
            width: 12px;
            height: 12px;
        }

        @include mixins.desktop-and-up {
            align-self: center;
        }
    }
}
